"use client";

/**
 * "N assumptions still need input" banner shown while the report is locked.
 *
 * Extracted from the design source `compass-oppreport.jsx` (`.rv-pending`).
 * The copy is the design's, verbatim (plan §9 #3), with one correction: the
 * prototype pluralises the noun on `count > 1` but never the verb, so a count
 * of 1 reads "1 assumption still need input". That is a typo in the prototype
 * rather than a design decision, so the verb agrees here.
 * The "Open assumptions →" link is a deliberate Atlas addition
 * and only appears when `onOpenAssumptions` is supplied; it switches the
 * sidebar to the existing assumptions tab and never edits it.
 */

import { Alert, ArrowR } from "../lib/icons";
import type { OpenAssumptionsHandler } from "./report-types";

export interface ReportPendingNoticeProps {
  count: number;
  /** Optional Atlas addition (plan §9 #3) — renders "Open assumptions →". */
  onOpenAssumptions?: OpenAssumptionsHandler;
}

/** `.rv-pending` — amber banner with an alert glyph, above the fold. */
export function ReportPendingNotice({
  count,
  onOpenAssumptions,
}: ReportPendingNoticeProps) {
  return (
    <div
      className={[
        "flex items-center gap-[9px] mb-5",
        "bg-(--decide-bg) border border-(--decide-border)",
        "rounded-(--radius-sm) px-[13px] py-[11px]",
        "text-[12.5px] text-(--status-warning-text)",
      ].join(" ")}
    >
      <span className="shrink-0 text-(--status-warning-text)">
        <Alert size={14} />
      </span>
      <span>
        {count} assumption{count === 1 ? " still needs" : "s still need"} input.
        Figures resolve once every value is entered.
      </span>
      {onOpenAssumptions && (
        <button
          type="button"
          className={[
            "ml-auto shrink-0 inline-flex items-center gap-[5px]",
            "font-[family-name:var(--font-body)] text-[12px] font-semibold",
            "text-(--fg-brand) cursor-pointer",
            "hover:underline hover:underline-offset-2",
          ].join(" ")}
          onClick={onOpenAssumptions}
        >
          Open assumptions
          <ArrowR size={12} />
        </button>
      )}
    </div>
  );
}
